import { FC } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  createStyles,
  Divider,
  Grid,
  Link,
  makeStyles,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { get, truncate } from "lodash";
import moment from "moment";

import { Button, ListingTerm } from "components";
import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { formatCurrencyAmount } from "lib/format";
import { getDealPageUrl } from "lib/helpers";
import { NON_FAILED_DEAL_STATE_TO_DISPLAY_NAME } from "services/offers/constants";
import { Offer } from "services/offers/types";
import theme from "theme";

const useStyles = makeStyles(() =>
  createStyles({
    card: {
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[3],
    },
    cardHeader: {
      textAlign: "left",
      fontSize: "1.7rem",
      "&:hover": {
        color: theme.palette.text.primary,
        cursor: "pointer",
        textDecoration: "none",
      },
    },
    listingTermRow: {
      paddingTop: 0,
    },
    updatedText: {
      fontSize: "0.8rem",
      color: theme.custom.palette.gray.dark,
    },
    columnHeader: {
      fontSize: "0.8rem",
      color: theme.custom.palette.gray.dark,
      paddingBottom: theme.spacing(1),
    },
    offerFieldValue: {
      [theme.breakpoints.down("xs")]: {
        fontSize: "0.9rem",
      },
    },
  }),
);

interface DealCardProps {
  offer: Offer;
}

const DealCard: FC<DealCardProps> = ({ offer }) => {
  const classes = useStyles();
  const isXsDown = useMediaQuery(theme.breakpoints.down("xs"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    acceptance_date,
    amount,
    deal_id,
    id: offerId,
    listing_title,
    state,
    term,
    updated_at,
  } = offer;

  const pendingOfferFieldsColumns = [
    {
      title: "Purchase Price",
      value: amount
        ? formatCurrencyAmount("USD", amount)
        : EMPTY_VALUE_PLACEHOLDER,
    },
    {
      title: "Purchase Date",
      value: acceptance_date
        ? moment(acceptance_date).format("MM/DD/YY")
        : EMPTY_VALUE_PLACEHOLDER,
    },
    {
      title: "Status",
      value: get(
        NON_FAILED_DEAL_STATE_TO_DISPLAY_NAME,
        state,
        EMPTY_VALUE_PLACEHOLDER,
      ),
    },
  ];

  const maybeTruncatedListingTitle = isSmDown
    ? listing_title
    : truncate(listing_title, { length: 43 });

  return (
    <Card className={classes.card}>
      <Box display="flex">
        <Box flexGrow={2}>
          <CardHeader
            classes={{ root: classes.cardHeader }}
            title={
              <Tooltip title={listing_title}>
                <Link
                  href={getDealPageUrl(offerId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.cardHeader}
                >
                  {maybeTruncatedListingTitle} {`(Deal #${deal_id})`}
                </Link>
              </Tooltip>
            }
          />
          <CardContent className={classes.listingTermRow}>
            <Box display="flex" justifyContent="space-between">
              <ListingTerm term={term} />
              <Box>
                <Typography variant="caption" className={classes.updatedText}>
                  {`Last Updated: ${moment(updated_at).format("MM/DD/YY")}`}
                </Typography>
              </Box>
            </Box>
          </CardContent>
          <Divider />
          <CardContent>
            <Grid container spacing={isXsDown ? 0 : 2}>
              {pendingOfferFieldsColumns.map((offerField, index) => {
                return (
                  <Grid item xs={4} key={index}>
                    <Box className={classes.columnHeader}>
                      {offerField.title}
                    </Box>
                    <Box className={classes.offerFieldValue}>
                      {offerField.value}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
          <Divider />
          <CardContent>
            <Link
              href={getDealPageUrl(offerId)}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.cardHeader}
            >
              <Button variant="contained" color="primary" fullWidth>
                Details
              </Button>
            </Link>
          </CardContent>
        </Box>
      </Box>
    </Card>
  );
};

export default DealCard;
